import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core'; // Importamos Optional
import { AppState } from '@core/store';
import { Logout } from '@core/store/actions/auth.actions';
import { Store } from '@ngrx/store';
import { DefaultMessageText, MessageUtilService, TypeMessage } from '@utils/services/message-util.service';
import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { InterceptorsStrategyService } from '../InterceptorsStrategy.service';

@Injectable({ providedIn: 'root' })
export class ErrorHandlerInterceptor implements HttpInterceptor {
	private refreshTokenInProgress = false;
	private tokenSubject = new BehaviorSubject<boolean>(false);

	private readonly serverErrorReasonByStatus: Record<number, string> = {
		403: 'Error de autenticación (403)',
		500: 'Error interno del servidor (500)',
		501: 'Funcionalidad no implementada (501)',
		502: 'Puerta de enlace inválida (502)',
		503: 'Servicio no disponible (503)',
		504: 'Tiempo de espera agotado (504)',
		505: 'Versión HTTP no soportada (505)',
		506: 'Variación no negociable (506)',
		507: 'Almacenamiento insuficiente (507)',
		508: 'Bucle detectado (508)',
		510: 'Extensión no reconocida (510)',
		511: 'Autenticación requerida (511)',
	};

	constructor(private readonly interceptorsStrategyService: InterceptorsStrategyService, private readonly messageUtil: MessageUtilService, private readonly store: Store<AppState>) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const strategy = this.interceptorsStrategyService.resolveStrategy(req);

		return next.handle(req).pipe(
			catchError((error: HttpErrorResponse) => {
				if (!strategy) {
					return this.handleErrorChain(error);
				}
				if (strategy.refreshToken && strategy.isPosibleTokenExpired(error)) {
					if (!this.refreshTokenInProgress) {
						this.refreshTokenInProgress = true;
						this.tokenSubject.next(false);

						return strategy.refreshToken(req).pipe(
							catchError((error: HttpErrorResponse) => {
								this.refreshTokenInProgress = false;
								return this.handleLogout(error);
							}),
							switchMap((resultValue) => {
								this.refreshTokenInProgress = false;
								this.tokenSubject.next(true);
								return next.handle(strategy.getHttpRequest(req, resultValue)).pipe(
									catchError((error: HttpErrorResponse) => {
										this.refreshTokenInProgress = false;
										if (strategy.isPosibleTokenExpired(error)) {
											return this.handleLogout(error);
										} else {
											return this.handleErrorChain(error);
										}
									})
								);
							})
						);
					}

					return this.tokenSubject.pipe(
						filter((ready) => ready),
						take(1),
						mergeMap(() => {
							if (!strategy.additionalParams) {
								return of({});
							}
							return strategy.additionalParams(req);
						}),
						switchMap((resultValue) => {
							return next.handle(strategy.getHttpRequest(req, resultValue));
						})
					);
				}
				return this.handleErrorChain(error);
			})
		);
	}

	private handleErrorChain(error: HttpErrorResponse): Observable<never> {
		this.messageUtil.closeLoading();
		if (error.error instanceof Blob) {
			error.error.text().then((text) => {
				try {
					this.processErrorMessage(JSON.parse(text), error);
				} catch (e) {
					this.messageUtil.getAlertError('Error de servidor');
				}
			});
		} else {
			this.processErrorMessage(error.error, error);
		}
		return throwError(() => error);
	}

	private handleLogout(error: HttpErrorResponse): Observable<never> {
		return from(
			Swal.fire({
				icon: 'warning',
				title: 'Sesión expirada',
				text: 'Tu sesión ha caducado por seguridad. Por favor, vuelve a iniciar sesión.',
				confirmButtonText: 'Iniciar sesión nuevamente',
				allowOutsideClick: false,
				allowEscapeKey: false,
				allowEnterKey: true,
				showCancelButton: false,
				backdrop: true,
			})
		).pipe(
			switchMap(() => {
				this.store.dispatch(new Logout());
				return throwError(() => error);
			})
		);
	}

	private processErrorMessage(apiRes: any, rawError: HttpErrorResponse): void {
		if (!apiRes) {
			this.messageUtil.getAlertError(rawError.message || 'Error');
			return;
		}

		if (apiRes.type === 'A') {
			this.messageUtil.getAlertWarning(apiRes?.mensaje);
		} else if (apiRes.type === 'E') {
			if (apiRes?.mensaje) {
				if (apiRes?.titulo && apiRes?.titulo !== '') {
					this.messageUtil.getAlertError(apiRes?.mensaje, apiRes?.titulo);
				} else {
					this.messageUtil.getAlertError(apiRes?.mensaje, 'Error');
				}
			} else {
				this.messageUtil.getAlertError(apiRes?.titulo, 'Error');
			}
		} else {
			const reason = this.serverErrorReasonByStatus[rawError.status] || 'Error desconocido';
			this.messageUtil.getAlertError(reason);
		}
	}
}
